/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { FieldGroup, FieldNumber } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { IInspectorControl_Float, TInspectorControlProps } from "../InspectorControlTypes";
import { isModelValueRequired } from "@adapptio/lib-blueprint";

export interface IInspectorControlFloatProps extends TInspectorControlProps<IInspectorControl_Float> {}

export const schemaName = "constFloat";

export const InspectorControlFloat: React.FC<IInspectorControlFloatProps> = ({ model: _model }) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constFloat");

	const required = isModelValueRequired(model);

	const inspectorContext = useEditorInspectorContext();

	const handleChange = useCallback(
		(value: number) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldGroup grow>
			<FieldNumber
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				min={model.schema.opts.constraints?.min}
				max={model.schema.opts.constraints?.max}
				clearable={!required}
			/>
		</FieldGroup>
	);
};
